/**
 * Object Selection Context
 *
 * Enables cross-module navigation by allowing one module to request
 * that another module select/open a specific object.
 *
 * Usage:
 * - Activity Feed can call selectObject('note', noteId)
 * - Notes module listens and opens that note
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

interface ObjectSelection {
  // 'doc' (ADR 023, #3787) joins 'note': a migrated Drive doc selected
  // cross-module (e.g. a chat citation) arrives as type='doc'.
  type: 'task' | 'note' | 'doc' | 'event' | 'contact' | 'place' | 'asset' | 'stat' | 'email';
  id: string;
  timestamp: number; // Used to detect new selections
  // Optional extra context for the handling module (e.g. a recurring event's
  // occurrence date). Persisted with the selection so it survives the target
  // module mounting AFTER selectObject is called.
  meta?: Record<string, unknown>;
}

interface ObjectSelectionContextValue {
  // Current selection request
  selection: ObjectSelection | null;

  // Request to select an object (triggers modules to respond)
  selectObject: (type: ObjectSelection['type'], id: string, meta?: Record<string, unknown>) => void;

  // Clear selection (after module handles it)
  clearSelection: () => void;
}

const ObjectSelectionContext = createContext<ObjectSelectionContextValue | undefined>(undefined);

export function ObjectSelectionProvider({ children }: { children: ReactNode }) {
  const [selection, setSelection] = useState<ObjectSelection | null>(null);

  const selectObject = useCallback(
    (type: ObjectSelection['type'], id: string, meta?: Record<string, unknown>) => {
      setSelection({ type, id, timestamp: Date.now(), meta });
    },
    [],
  );

  const clearSelection = useCallback(() => {
    setSelection(null);
  }, []);

  return (
    <ObjectSelectionContext.Provider value={{ selection, selectObject, clearSelection }}>
      {children}
    </ObjectSelectionContext.Provider>
  );
}

export function useObjectSelection() {
  const context = useContext(ObjectSelectionContext);
  if (!context) {
    throw new Error('useObjectSelection must be used within an ObjectSelectionProvider');
  }
  return context;
}

/**
 * Hook to listen for object selection of a specific type
 * Returns the selected ID when one is requested, or null
 */
export function useObjectSelectionListener(
  type: ObjectSelection['type'],
  onSelect: (id: string, meta?: Record<string, unknown>) => void
) {
  const { selection, clearSelection } = useObjectSelection();
  const lastHandledRef = useRef<number>(0);

  useEffect(() => {
    if (
      selection &&
      selection.type === type &&
      selection.timestamp > lastHandledRef.current
    ) {
      lastHandledRef.current = selection.timestamp;
      onSelect(selection.id, selection.meta);
      clearSelection();
    }
  }, [selection, type, onSelect, clearSelection]);
}
